import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Alert, Spinner, Badge } from '@/components/ui';
import { workIdService } from '../../../services/workIdService';
import type { WorkIdListItem } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { WorkIdRecord } from '../workIdService';

interface WorkIdMergeModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdRecord | null;
  onSuccess: () => void;
}

const WorkIdMergeModal: React.FC<WorkIdMergeModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [allWorkIds, setAllWorkIds] = useState<WorkIdListItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [search, setSearch] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkIds = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await workIdService.getList({ limit: 5000 });
      setAllWorkIds(list);
    } catch {
      setAllWorkIds([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (visible && workId?.id) {
      setError(null);
      setComments('');
      setSelectedTargetId('');
      setSearch('');
      fetchWorkIds();
    }
  }, [visible, workId?.id, fetchWorkIds]);

  const filteredWorkIds = useMemo(() => {
    const excluding = workId?.id;
    let list = allWorkIds.filter((w) => w.id !== excluding);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((w) =>
        (w.workIdentificationId || '').toLowerCase().includes(q) ||
        String(w.id).includes(q) ||
        (w.comments || '').toLowerCase().includes(q) ||
        (w.status || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allWorkIds, workId?.id, search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId) return;
    const targetId = parseInt(selectedTargetId, 10);
    if (isNaN(targetId) || targetId <= 0) {
      setError('Please select a target Work ID.');
      return;
    }
    if (targetId === workId.id) {
      setError('Target Work ID must be different from the current one.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await workIdService.merge(workId.id, {
        targetWorkIdId: targetId,
        comments: comments || undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge work ID');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedWorkId = allWorkIds.find((w) => String(w.id) === selectedTargetId);

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting || !selectedTargetId}>
        {submitting ? 'Merging…' : 'Merge'}
      </Button>
    </>
  );

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={`Merge Work ID ${workId?.workIdentificationId ?? ''} into another`}
      footer={footer}
      size="medium"
    >
      {workId && (
        <Form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" className="tw-py-2">
              {error}
            </Alert>
          )}

          <Form.Group className="tw-mb-3">
            <Form.Label className="tw-font-semibold">Target Work ID</Form.Label>
            <Form.Text className="tw-block tw-mb-2 tw-text-content-muted">
              This work ID will be merged into the selected target. The target will remain.
            </Form.Text>

            {loadingList ? (
              <div className="tw-text-center tw-py-3">
                <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden />
                Loading work IDs…
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Search by ID, name, comments, or status…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="tw-mb-2"
                  size="sm"
                />
                <Form.Select
                  value={selectedTargetId}
                  onChange={(e) => setSelectedTargetId(e.target.value)}
                  size="sm"
                  style={{ height: 220 }}
                  htmlSize={10}
                >
                  {filteredWorkIds.map((w) => (
                    <option key={w.id} value={String(w.id)}>
                      {w.workIdentificationId || `#${w.id}`} – {w.status} – {(w.comments || '').slice(0, 60) || 'No comments'}
                    </option>
                  ))}
                  {filteredWorkIds.length === 0 && (
                    <option disabled value="">No work IDs found</option>
                  )}
                </Form.Select>
                <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                  {filteredWorkIds.length} work ID{filteredWorkIds.length !== 1 ? 's' : ''} available
                </div>
              </>
            )}
          </Form.Group>

          {selectedWorkId && (
            <div className="tw-border tw-border-line tw-rounded tw-p-2 tw-mb-3 tw-bg-surface-alt tw-text-sm">
              <strong>Selected:</strong>{' '}
              {selectedWorkId.workIdentificationId || `#${selectedWorkId.id}`}{' '}
              <Badge bg="secondary" className="tw-ml-1">{selectedWorkId.status}</Badge>
              {selectedWorkId.comments && (
                <div className="tw-text-content-muted tw-mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedWorkId.comments.slice(0, 200)}
                  {selectedWorkId.comments.length > 200 ? '…' : ''}
                </div>
              )}
            </div>
          )}

          <Form.Group className="tw-mb-3">
            <Form.Label>Comments (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional comments"
            />
          </Form.Group>
        </Form>
      )}
    </ResponsiveModal>
  );
};

export default WorkIdMergeModal;
